import { computed, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { interval, Observable, pipe, startWith, switchMap, tap } from 'rxjs';
import { CustomerRemarkModel, CustomerRemarkSearchParamModel, CustomerRemarkService } from '@railmybox/api-user';

type NotificationCenterState = {
  unreadNotificationCount: number;
  notifications: CustomerRemarkModel[];
  isLoadingNotifications: boolean;
  searchParams: CustomerRemarkSearchParamModel;
};

const INITIAL_STATE: NotificationCenterState = {
  unreadNotificationCount: 0,
  notifications: [],
  isLoadingNotifications: false,
  searchParams: undefined,
};

export const NotificationCenterStore = signalStore(
  { providedIn: 'root' },
  withState(INITIAL_STATE),
  withComputed(({ notifications }) => ({
    todayNotifications: computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      return notifications().filter((n) => new Date(n.creation) >= today);
    }),

    yesterdayNotifications: computed(() => {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      return notifications().filter((n) => new Date(n.creation) >= yesterday && new Date(n.creation) < today);
    }),

    olderNotifications: computed(() =>
      notifications().filter((n) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        return new Date(n.creation) < yesterday;
      })
    ),
  })),

  withMethods((store, customerRemarkService = inject(CustomerRemarkService)) => {
    const getFilteredNotifications = (searchParams: CustomerRemarkSearchParamModel): Observable<CustomerRemarkModel[]> => {
      patchState(store, { isLoadingNotifications: true });
      return customerRemarkService.listRemarks(searchParams, 50).pipe(
        tap((notifications) => {
          const notificationsWithFixedHTMLText = notifications.map((notification) => ({
            ...notification,
            text: notification.text.replace(/&nbsp;/g, ' '),
          }));
          patchState(store, { notifications: notificationsWithFixedHTMLText, searchParams });
        }),
        tap(() => patchState(store, { isLoadingNotifications: false }))
      );
    };

    const fetchNotifications = rxMethod<CustomerRemarkSearchParamModel>(
      pipe(
        tap(() => patchState(store, { isLoadingNotifications: true })),
        switchMap((searchParams) => getFilteredNotifications(searchParams))
      )
    );

    const fetchLoopNotifications = rxMethod<void>(
      pipe(
        switchMap(() =>
          interval(2 * 60 * 1000).pipe(
            startWith(0),
            switchMap(() =>
              customerRemarkService.listRemarks({ status: 'NEW' }, 10).pipe(
                tap((loopedNotifications) => {
                  const unreadNotificationCount = loopedNotifications?.length;
                  patchState(store, { unreadNotificationCount });
                })
              )
            )
          )
        )
      )
    );

    const markAsRead = rxMethod<string>(
      pipe(switchMap((id) => customerRemarkService.readRemark(id).pipe(switchMap(() => getFilteredNotifications(store.searchParams())))))
    );

    const markAsNew = rxMethod<string>(
      pipe(switchMap((id) => customerRemarkService.unreadRemark(id).pipe(switchMap(() => getFilteredNotifications(store.searchParams())))))
    );

    const markAsTask = rxMethod<string>(
      pipe(switchMap((id) => customerRemarkService.taskRemark(id).pipe(switchMap(() => getFilteredNotifications(store.searchParams())))))
    );

    const markAsNotTask = rxMethod<string>(
      pipe(switchMap((id) => customerRemarkService.untaskRemark(id).pipe(switchMap(() => getFilteredNotifications(store.searchParams())))))
    );

    return {
      getFilteredNotifications,
      fetchNotifications,
      fetchLoopNotifications,
      markAsRead,
      markAsNew,
      markAsTask,
      markAsNotTask,
    };
  }),

  withHooks({
    onInit(store) {
      store.fetchLoopNotifications();
    },
  })
);
